/**
 * Session management service for multi-session AR support.
 *
 * Handles:
 * - Session acquisition and storage
 * - Session release on window unload
 * - Heartbeat to keep session alive
 * - Session error handling
 */

const SESSION_STORAGE_KEY = 'ar_session_id';
const BROWSER_ID_KEY = 'ar_browser_id';
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Generate a unique browser ID for this tab/window.
 * This ensures multiple users with same credentials get separate sessions.
 */
function generateBrowserId() {
  return 'browser-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
}

/**
 * Get or create browser ID for this tab.
 * Uses sessionStorage so each tab gets its own ID.
 */
function getBrowserId() {
  let browserId = sessionStorage.getItem(BROWSER_ID_KEY);
  if (!browserId) {
    browserId = generateBrowserId();
    sessionStorage.setItem(BROWSER_ID_KEY, browserId);
  }
  return browserId;
}

class SessionService {
  constructor() {
    this.sessionId = null;
    this.browserId = null;
    this.heartbeatTimer = null;
    this.isAcquiring = false;
    this.initialized = false;
  }

  /**
   * Initialize session service.
   * Uses unique browser ID per tab to support multiple users with same credentials.
   */
  async initialize() {
    // Generate unique ID per browser tab - ensures separate sessions even with shared credentials
    this.browserId = getBrowserId();

    // Check for existing session in storage
    const storedSession = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (storedSession) {
      const isValid = await this.validateSession(storedSession);
      if (isValid) {
        this.sessionId = storedSession;
        this.startHeartbeat();
        this.initialized = true;
        console.log(`[Session] Restored session: ${this.sessionId}`);
        return this.sessionId;
      }
    }

    // Acquire new session
    return this.acquireSession();
  }

  /**
   * Validate if a session is still active.
   */
  async validateSession(sessionId) {
    try {
      const response = await fetch('/session/status', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-AR-Session': sessionId
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.sessions && data.sessions[sessionId] && data.sessions[sessionId].is_active;
      }
      return false;
    } catch (error) {
      console.error('[Session] Validation error:', error);
      return false;
    }
  }

  /**
   * Acquire a new session from the server.
   */
  async acquireSession() {
    if (this.isAcquiring) {
      // Wait for ongoing acquisition
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (!this.isAcquiring) {
            clearInterval(checkInterval);
            if (this.sessionId) {
              resolve(this.sessionId);
            } else {
              reject(new Error('Session acquisition failed'));
            }
          }
        }, 100);
      });
    }

    this.isAcquiring = true;

    try {
      const response = await fetch('/session/acquire', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        // Use unique browser_id instead of user_email
        // This ensures each tab gets its own session even with shared credentials
        body: JSON.stringify({ browser_id: this.browserId })
      });

      if (response.ok) {
        const data = await response.json();
        this.sessionId = data.session_id;
        sessionStorage.setItem(SESSION_STORAGE_KEY, this.sessionId);
        this.startHeartbeat();
        this.initialized = true;
        console.log(`[Session] Acquired new session: ${this.sessionId}`);
        return this.sessionId;
      } else if (response.status === 503) {
        const data = await response.json();
        throw new Error(data.message || 'All sessions are busy. Please try again later.');
      } else {
        throw new Error('Failed to acquire session');
      }
    } catch (error) {
      console.error('[Session] Acquisition error:', error);
      throw error;
    } finally {
      this.isAcquiring = false;
    }
  }

  /**
   * Release the current session.
   */
  async releaseSession() {
    if (!this.sessionId) return;

    this.stopHeartbeat();

    try {
      // Use sendBeacon for reliability during page unload
      const data = JSON.stringify({ session_id: this.sessionId });
      const blob = new Blob([data], { type: 'application/json' });
      navigator.sendBeacon('/session/release', blob);
      console.log(`[Session] Released session: ${this.sessionId}`);
    } catch (error) {
      console.error('[Session] Release error:', error);
    } finally {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      this.sessionId = null;
    }
  }

  /**
   * Send heartbeat to keep session alive.
   */
  async sendHeartbeat() {
    if (!this.sessionId) return;

    try {
      await fetch('/session/heartbeat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-AR-Session': this.sessionId
        }
      });
    } catch (error) {
      console.warn('[Session] Heartbeat failed:', error);
    }
  }

  /**
   * Start periodic heartbeat.
   */
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.sendHeartbeat();
    }, HEARTBEAT_INTERVAL);
  }

  /**
   * Stop heartbeat.
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Get current session ID.
   */
  getSessionId() {
    return this.sessionId;
  }

  /**
   * Check if session is initialized.
   */
  isInitialized() {
    return this.initialized && this.sessionId !== null;
  }
}

// Singleton instance
const sessionService = new SessionService();

// Release session on page unload
window.addEventListener('beforeunload', () => {
  sessionService.releaseSession();
});

export default sessionService;
